// src/components/posts/PostCard.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import CommentList from '../comments/CommentList';
import CommentForm from '../comments/CommentForm';
import ReactionButtons from './ReactionButtons';
import '../../styles/post-card.css';

const PostCard = ({ post, showComments = false, onUpdate = null, onReact = null }) => {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(showComments);
  const [isSharing, setIsSharing] = useState(false);
  const [shareText, setShareText] = useState('');
  const [comments, setComments] = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  
  // Create a fallback author object if post.author is missing
  const author = post.author || { 
    username: post.username, 
    profilePic: null, 
    displayName: post.username 
  };
  
  // Format date
  const formattedDate = formatDistanceToNow(new Date(post.createdAt), { addSuffix: true });
  
  // Check if this is a shared post
  const isSharedPost = post.metadata && post.metadata.type === 'share';
  
  // Load comments
  const loadComments = async () => {
    if (!commentsLoaded) {
      try {
        const response = await api.get(`/api/comments/post/${post.id}`);
        setComments(response.data.comments);
        setCommentsLoaded(true);
      } catch (error) {
        console.error('Error loading comments:', error);
      }
    }
  };
  
  // Toggle comments
  const toggleComments = () => {
    if (!expanded) {
      loadComments();
    }
    setExpanded(!expanded);
  };
  
  // Handle reaction
  // const handleReaction = async (reactionType) => {
  //   try {
  //     await api.post(`/api/posts/${post.id}/react`, { reactionType });
      
  //     // Update local state for UI
  //     if (onUpdate) {
  //       onUpdate();
  //     }
  //   } catch (error) {
  //     console.error('Error reacting to post:', error);
  //   }
  // };

  const handleReaction = async (reactionType) => {
    if (onReact) {
      onReact(reactionType);
    }
  };
  
  // Handle share
  const handleShare = async () => {
    try {
      const response = await api.post(`/api/posts/${post.id}/share`, { text: shareText });
      
      // Reset share form
      setIsSharing(false);
      setShareText('');
      
      // Add the shared post to the feed (typically handled by parent component)
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error sharing post:', error);
    }
  };
  
  // Handle new comment
  const handleNewComment = (newComment) => {
    setComments([newComment, ...comments]);
    
    // Update comment count
    if (onUpdate) {
      onUpdate();
    }
  };
  const getIMGURL = (url) => {
    if (!url) return '/default-avatar.png';
    
    if (url.startsWith('ipfs://')) {
      // Extract the CID (Content ID) from the IPFS URL
      const cid = url.replace('ipfs://', '');
      // Use a public IPFS gateway
      return `https://ipfs.io/ipfs/${cid}`;
    }
    
    return url;
  }
  
  return (
    <div className="post-card">
      {/* Post header */}
      <div className="post-header">
        <Link to={`/profile/${author.username}`} className="author-link">
          <img 
            src={getIMGURL(author.profilePic) || '/default-avatar.png'} 
            alt={author.username}
            className="author-avatar" 
          />
          <div className="author-info">
            <h4 className="author-name">{author.displayName || author.username}</h4>
            <span className="post-date">{formattedDate}</span>
          </div>
        </Link>
      </div>
      
      {/* Post content */}
      <div className="post-content">
        <p className="post-text">{post.text}</p>
        
        {post.image && (
          <div className="post-image-container">
            <img 
              src={post.image?.startsWith('ipfs://') 
                ? `https://ipfs.io/ipfs/${post.image.replace('ipfs://', '')}` 
                : post.image} 
              alt="Post" 
              className="post-image" 
            />
          </div>
        )}
        
        {/* Shared post */}
        {isSharedPost && post.sharedPost && (
          <div className="shared-post">
            <div className="shared-post-header">
              <span>Originally posted by {post.sharedPost.username}</span>
            </div>
            <div className="shared-post-content">
              <p>{post.sharedPost.text}</p>
              {post.sharedPost.image && (
                <div className="post-image-container">
                  <img 
                    src={post.sharedPost.image?.startsWith('ipfs://') 
                      ? `https://ipfs.io/ipfs/${post.sharedPost.image.replace('ipfs://', '')}` 
                      : post.sharedPost.image} 
                    alt="Shared post" 
                    className="post-image" 
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Post stats */}
      <div className="post-stats">
        <span>{post.reactions?.likes || 0} likes</span>
        <span>{post.commentCount || 0} comments</span>
        <span>{post.reactions?.shares || 0} shares</span>
      </div>
      
      {/* Reaction buttons */}
      <ReactionButtons 
        onLike={() => handleReaction('like')}
        onComment={toggleComments}
        onShare={() => setIsSharing(!isSharing)}
        isLiked={post.userReaction === 'like'}
      />
      
      {/* Share form */}
      {isSharing && (
        <div className="share-form">
          <textarea
            placeholder="Add a comment to your share..."
            value={shareText}
            onChange={(e) => setShareText(e.target.value)}
          />
          <div className="share-actions">
            <button onClick={() => setIsSharing(false)}>Cancel</button>
            <button onClick={handleShare}>Share</button>
          </div>
        </div>
      )}
      
      {/* Comments */}
      {expanded && (
        <div className="comments-section">
          <CommentForm postId={post.id} onCommentAdded={handleNewComment} />
          
          <CommentList comments={comments} loading={!commentsLoaded} />
          
          {comments.length > 0 && !showComments && (
            <Link to={`/post/${post.id}`} className="view-all-comments">
              View all comments
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default PostCard;